import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import {
  BaseAvatarSize,
  BaseButtonSize,
  BaseCoreState,
  BaseDateService,
  BaseDeliveryTimeListItemExtendedInterface,
  baseInquiryActions,
  baseInquirySelectors,
  BaseOrderDirectionEnum,
} from 'base';
import { BaseCustomerInquiredServiceProvidersSortFieldsEnum } from '../../_enums/base-customer-inquired-service-providers-sort-fields.enum';

@Component({
  selector: 'base-customer-inquired-service-providers-modal',
  templateUrl: './base-customer-inquired-service-providers-modal.component.html',
})
export class BaseCustomerInquiredServiceProvidersModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) inquiryId: number;
  @Input({ required: true }) isInquiryClosed: boolean;
  @Input() inquiryClosingDate: string | undefined;

  availableServiceProviders: BaseDeliveryTimeListItemExtendedInterface[] = [];
  availableServiceProvidersFiltered: BaseDeliveryTimeListItemExtendedInterface[] = [];
  orderDirection: BaseOrderDirectionEnum | undefined;
  orderField: BaseCustomerInquiredServiceProvidersSortFieldsEnum;
  searchQuery: string;
  areAllProvidersSelected = false;
  amountOfInquiredServiceProviders: number;
  amountOfSentServiceProviders: number;
  isSendButtonDisabled: boolean;
  isSelectAllCheckboxDisabled: boolean;

  buttonSizes = BaseButtonSize;
  avatarSize = BaseAvatarSize;

  private destroy$ = new Subject<void>();

  constructor(private store: Store<BaseCoreState>) {}

  ngOnInit() {
    this.orderField = BaseCustomerInquiredServiceProvidersSortFieldsEnum.default;
    this.store
      .select(baseInquirySelectors.getInquiryProvidersList)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data)
      )
      .subscribe((providers) => {
        this.availableServiceProviders = this.defineProvidersAdditionalProperties(providers);
        this.availableServiceProvidersFiltered = [...this.availableServiceProviders];
        this.defineAmountOfInquiredAndSentServiceProviders();
        this.checkSendButtonDisabled();
        this.checkSelectAllCheckboxDisabled();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filterAvailableServiceProviders(searchQuery: string): void {
    this.searchQuery = searchQuery;
    if (this.areAllProvidersSelected) {
      this.selectAllProviders(false);
    }
    this.availableServiceProvidersFiltered = this.availableServiceProviders
      .filter(
        (item) =>
          item.pspBranch.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          item.psp.legalName?.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
      .map((item) => {
        const existingItem = this.availableServiceProvidersFiltered.find(
          (filteredItem) => filteredItem.pspBranch.id === item.pspBranch.id
        );
        return {
          ...item,
          isSelected: existingItem ? existingItem.isSelected : item.isSelected,
        };
      });

    this.defineAmountOfInquiredAndSentServiceProviders();
    this.checkSendButtonDisabled();
  }

  toggleProviderSelection(event: { branchId: number; isSelected: boolean }): void {
    const providerIndex = this.availableServiceProvidersFiltered.findIndex(
      (item) => item.pspBranch.id === event.branchId
    );

    if (providerIndex === -1) {
      return;
    }

    this.availableServiceProvidersFiltered[providerIndex] = {
      ...this.availableServiceProvidersFiltered[providerIndex],
      isSelected: event.isSelected,
    };

    this.areAllProvidersSelected = this.availableServiceProvidersFiltered.every((provider) => {
      const isProviderPendingToBeSent = provider.scheduledSentAt
        ? BaseDateService.isAfter(BaseDateService.toDate(provider.scheduledSentAt, 'yyyy-MM-dd HH:mm'), new Date())
        : false;
      const isProviderCanBeSelected = !this.isInquiryClosed && (!provider.scheduledSentAt || isProviderPendingToBeSent);

      return !isProviderCanBeSelected || provider.isSelected;
    });

    this.checkSendButtonDisabled();
  }

  selectAllProviders(event: boolean): void {
    this.availableServiceProvidersFiltered = this.availableServiceProvidersFiltered.map((provider) => {
      const isProviderPendingToBeSent = provider.scheduledSentAt
        ? BaseDateService.isAfter(BaseDateService.toDate(provider.scheduledSentAt, 'yyyy-MM-dd HH:mm'), new Date())
        : false;
      const isProviderCanBeSelected = !this.isInquiryClosed && (!provider.scheduledSentAt || isProviderPendingToBeSent);

      return isProviderCanBeSelected
        ? {
            ...provider,
            isSelected: event,
          }
        : provider;
    });

    this.areAllProvidersSelected = event;
    this.checkSendButtonDisabled();
  }

  sortAvailableServiceProviders({
    key,
    direction,
  }: {
    key: string;
    direction: BaseOrderDirectionEnum | undefined;
  }): void {
    this.orderDirection = direction;

    if (!direction) {
      this.availableServiceProvidersFiltered = this.availableServiceProviders.map((item) => {
        const existingItem = this.availableServiceProvidersFiltered.find(
          (filteredItem) => filteredItem.pspBranch.id === item.pspBranch.id
        );
        return {
          ...item,
          isSelected: existingItem ? existingItem.isSelected : item.isSelected,
        };
      });
      return;
    }

    this.sortProviders(key);
  }

  sendImmediately(): void {
    const branches = this.availableServiceProvidersFiltered
      .filter((item) => item.isSelected && item.isCheckBoxAvailable)
      .map((item) => item.pspBranch.id);

    this.store.dispatch(
      baseInquiryActions.sendInquiryProvidersImmediately({ payload: { inquiry: this.inquiryId, body: { branches } } })
    );
    this.resetSearchAndSorting();
  }

  private defineProvidersAdditionalProperties(
    providers: BaseDeliveryTimeListItemExtendedInterface[]
  ): BaseDeliveryTimeListItemExtendedInterface[] {
    return providers.map((provider) => {
      const isPendingToBeSent = provider.scheduledSentAt
        ? BaseDateService.isAfter(BaseDateService.toDate(provider.scheduledSentAt, 'yyyy-MM-dd HH:mm'), new Date())
        : false;

      const isInquiryClosedBeforeSent =
        this.isInquiryClosed && this.inquiryClosingDate && provider.scheduledSentAt
          ? BaseDateService.isAfter(
              BaseDateService.toDate(provider.scheduledSentAt, 'yyyy-MM-dd HH:mm'),
              BaseDateService.toDate(this.inquiryClosingDate, 'yyyy-MM-dd HH:mm')
            )
          : false;

      const isCheckBoxAvailable = !this.isInquiryClosed && (!provider.scheduledSentAt || isPendingToBeSent);

      return {
        ...provider,
        isPendingToBeSent,
        isInquiryClosedBeforeSent,
        isCheckBoxAvailable,
      };
    });
  }

  private defineAmountOfInquiredAndSentServiceProviders(): void {
    this.amountOfInquiredServiceProviders = this.availableServiceProvidersFiltered.filter(
      (item) => !!item.scheduledSentAt
    ).length;

    this.amountOfSentServiceProviders = this.availableServiceProviders.filter(
      (item) =>
        !!item.scheduledSentAt &&
        ((!this.isInquiryClosed &&
          BaseDateService.isAfter(new Date(), BaseDateService.toDate(item.scheduledSentAt, 'yyyy-MM-dd HH:mm'))) ||
          (this.isInquiryClosed && !item.isInquiryClosedBeforeSent))
    ).length;
  }

  private compareByBranch(
    a: BaseDeliveryTimeListItemExtendedInterface,
    b: BaseDeliveryTimeListItemExtendedInterface
  ): number {
    if (a.pspBranch.name < b.pspBranch.name) return -1;
    if (a.pspBranch.name > b.pspBranch.name) return 1;
    return 0;
  }

  private compareByPriority(
    a: BaseDeliveryTimeListItemExtendedInterface,
    b: BaseDeliveryTimeListItemExtendedInterface
  ): number {
    if (a.deliveryType < b.deliveryType) return -1;
    if (a.deliveryType > b.deliveryType) return 1;
    return 0;
  }

  private compareBySendingTime(
    a: BaseDeliveryTimeListItemExtendedInterface,
    b: BaseDeliveryTimeListItemExtendedInterface
  ): number {
    const dateA = a.scheduledSentAt ? BaseDateService.toDate(a.scheduledSentAt, 'yyyy-MM-dd HH:mm') : null;
    const dateB = b.scheduledSentAt ? BaseDateService.toDate(b.scheduledSentAt, 'yyyy-MM-dd HH:mm') : null;

    if (dateA && dateB) {
      return BaseDateService.isBefore(dateA, dateB) ? -1 : BaseDateService.isAfter(dateA, dateB) ? 1 : 0;
    }
    return 0;
  }

  private sortProviders(field: string): void {
    this.availableServiceProvidersFiltered = [...this.availableServiceProvidersFiltered].sort((a, b) => {
      if (a.scheduledSentAt && !b.scheduledSentAt) return -1;
      if (!a.scheduledSentAt && b.scheduledSentAt) return 1;

      switch (field) {
        case BaseCustomerInquiredServiceProvidersSortFieldsEnum.branch:
          return this.orderDirection === BaseOrderDirectionEnum.asc
            ? this.compareByBranch(a, b)
            : -this.compareByBranch(a, b);
        case BaseCustomerInquiredServiceProvidersSortFieldsEnum.priority:
          return this.orderDirection === BaseOrderDirectionEnum.asc
            ? this.compareByPriority(a, b)
            : -this.compareByPriority(a, b);
        case BaseCustomerInquiredServiceProvidersSortFieldsEnum.sendingTime:
          return this.orderDirection === BaseOrderDirectionEnum.asc
            ? this.compareBySendingTime(a, b)
            : -this.compareBySendingTime(a, b);
        default:
          return 0;
      }
    });
  }

  private checkSendButtonDisabled(): void {
    this.isSendButtonDisabled =
      this.availableServiceProvidersFiltered.length === 0 ||
      this.availableServiceProvidersFiltered.every((provider) => !provider.isSelected) ||
      this.amountOfSentServiceProviders === this.availableServiceProviders.length;
  }

  private checkSelectAllCheckboxDisabled(): void {
    this.isSelectAllCheckboxDisabled =
      this.isInquiryClosed || this.availableServiceProvidersFiltered.every((provider) => !provider.isCheckBoxAvailable);
  }

  private resetSearchAndSorting(): void {
    this.areAllProvidersSelected = false;
    this.orderDirection = undefined;
    this.orderField = BaseCustomerInquiredServiceProvidersSortFieldsEnum.default;
    this.searchQuery = '';
  }
}
